import type { ReactElement, ReactNode } from 'react';
import { createContext, Fragment, useCallback, useContext, useState } from 'react';
import { observer } from 'mobx-react';

import { useOutsideClick } from '@genially/design-system';

import { Container, PositionalContainer, Wrapper } from './Modal.styled';

export interface ModalProviderContextType {
  openModal: (
    modal: ((parentElement: HTMLDivElement) => ReactElement) | ReactElement,
    options?: { blocksBackground?: boolean },
  ) => string;
  closeModal: (modalId?: string) => void;
}

const ModalProviderContext = createContext({});

export interface ModalProviderProps {
  children: ReactNode;
}

type OpenedModal = {
  element: ReactElement;
  blocksBackground?: boolean;
};

const ModalProvider = observer(({ children }: ModalProviderProps) => {
  const [openedModals, setOpenedModals] = useState<Record<string, OpenedModal>>({});

  const hasOpenedModals = Object.keys(openedModals).length > 0;

  // Some modals (e.g. the initial session config, which can't be dismissed by clicking
  // outside) need the dimming overlay to sit above every bit of app UI, including the
  // absolutely-positioned, higher-z-index game control header — everyone else keeps the
  // default overlay z-index.
  const hasBlockingModal = Object.values(openedModals).some(
    modal => modal.blocksBackground,
  );

  const containerRef = useOutsideClick(() => {
    // A blocking modal (see hasBlockingModal above) must not be dismissable by clicking
    // outside it either — this outside-click watcher is separate from, and would otherwise
    // bypass, the modal's own shouldCloseOnOverlayClick={false}.
    if (hasOpenedModals && !hasBlockingModal) {
      setOpenedModals({});
    }
  });

  const openModal = useCallback<ModalProviderContextType['openModal']>(
    (modal, options) => {
      const id = Math.random().toString(36).substring(7);

      const container = containerRef.current;

      if (container) {
        const ModalComponent = typeof modal === 'function' ? modal(container) : modal;

        setOpenedModals(prev => {
          const currentModalsCount = Object.keys(prev).length;

          return {
            ...prev,
            [id]: {
              blocksBackground: options?.blocksBackground,
              element: (
                <PositionalContainer $zIndex={currentModalsCount + 10_000}>
                  {ModalComponent}
                </PositionalContainer>
              ),
            },
          };
        });
      }

      return id;
    },
    [containerRef],
  );

  const closeModal = useCallback<ModalProviderContextType['closeModal']>(
    (modalId?: string) => {
      if (modalId) {
        setOpenedModals(prev => {
          const newModals = { ...prev };
          delete newModals[modalId];
          return newModals;
        });
      } else {
        setOpenedModals({});
      }
    },
    [],
  );

  const context: ModalProviderContextType = {
    openModal,
    closeModal,
  };

  return (
    <ModalProviderContext.Provider value={context}>
      <Wrapper $show={hasOpenedModals} $blocksBackground={hasBlockingModal}>
        <Container ref={containerRef}>
          {Object.entries(openedModals).map(([key, modal]) => (
            <Fragment key={key}>{modal.element}</Fragment>
          ))}
        </Container>
      </Wrapper>
      {children}
    </ModalProviderContext.Provider>
  );
});

const useModal = () => {
  const context = useContext(ModalProviderContext) as ModalProviderContextType;

  if (Object.keys(context).length === 0) {
    throw new Error('useModalProvider must be used within a ModalProvider');
  }

  return context;
};

export { ModalProvider, useModal };
